import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  format, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  isSameMonth, 
  isSameDay, 
  addDays, 
  isWithinInterval,
  isBefore,
  parseISO,
  eachDayOfInterval
} from 'date-fns';
import { es, enUS, ptBR } from 'date-fns/locale';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronLeft, FiChevronRight, FiLoader, FiCalendar, FiCheckCircle, FiArrowRight } = FiIcons;

export default function CalendarView({ onReserve, t, lang }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [blockedRanges, setBlockedRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState({ start: null, end: null });

  const locales = { es, en: enUS, pt: ptBR };
  const currentLocale = locales[lang] || es;

  useEffect(() => {
    fetchOccupiedDates();
  }, []);

  const fetchOccupiedDates = async () => {
    try {
      setLoading(true);
      const res = await fetch('https://92afb.twidget.io/availability');
      const json = await res.json();
      const icalText = json.data;
      const blocked = [];
      const events = icalText.split('BEGIN:VEVENT');
      events.slice(1).forEach(event => {
        const startMatch = event.match(/DTSTART[^:]*:(\d{8})/);
        const endMatch = event.match(/DTEND[^:]*:(\d{8})/);
        if (startMatch && endMatch) {
          const s = startMatch[1], e = endMatch[1];
          blocked.push({ start: `${s.slice(0,4)}-${s.slice(4,6)}-${s.slice(6,8)}`, end: `${e.slice(0,4)}-${e.slice(4,6)}-${e.slice(6,8)}` });
        }
      });
      setBlockedRanges(blocked);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const isBlocked = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return blockedRanges.some(range => dateStr >= range.start && dateStr < range.end);
  };

  const isPast = (date) => {
    const today = new Date(); today.setHours(0,0,0,0);
    return isBefore(date, today);
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth), monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart), endDate = endOfWeek(monthEnd);
    const dayLabels = lang === 'es' ? ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'] : 
                      lang === 'pt' ? ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] :
                      ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const rows = []; let days = []; let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day, blocked = isBlocked(day), past = isPast(day), isToday = isSameDay(day, new Date());
        const isSelected = (selectedRange.start && isSameDay(day, selectedRange.start)) || (selectedRange.end && isSameDay(day, selectedRange.end));
        const isInRange = selectedRange.start && selectedRange.end && isWithinInterval(day, { start: selectedRange.start, end: selectedRange.end });
        days.push(
          <div key={day.toString()} onClick={() => {
            if (blocked || past) return;
            if (!selectedRange.start || (selectedRange.start && selectedRange.end)) setSelectedRange({ start: cloneDay, end: null });
            else if (isBefore(cloneDay, selectedRange.start)) setSelectedRange({ start: cloneDay, end: null });
            else {
              const daysInRange = eachDayOfInterval({ start: selectedRange.start, end: cloneDay });
              if (daysInRange.some(d => isBlocked(d))) setSelectedRange({ start: cloneDay, end: null });
              else setSelectedRange(p => ({ ...p, end: cloneDay }));
            }
          }} className={`relative h-14 flex flex-col items-center justify-center text-sm font-bold cursor-pointer transition-all ${!isSameMonth(day, monthStart) ? 'text-gray-300' : 'text-gray-700'} ${blocked ? 'bg-red-100 text-red-500 cursor-not-allowed' : 'bg-white hover:bg-emerald-50'} ${past && !blocked ? 'opacity-30' : ''} ${isToday ? 'border-2 border-emerald-500 rounded-xl' : ''} ${isSelected ? 'bg-emerald-800 text-white !rounded-xl' : ''} ${isInRange && !isSelected ? 'bg-emerald-50 text-emerald-800' : ''}`}>
            <span>{format(day, 'd')}</span>
            {blocked && <div className="absolute bottom-1 w-1 h-1 bg-red-400 rounded-full"></div>}
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(<div className="grid grid-cols-7" key={day.toString()}>{days}</div>);
      days = [];
    }
    return (
      <div className="bg-white rounded-3xl p-4 shadow-sm border border-gray-100">
        <div className="grid grid-cols-7 mb-4">{dayLabels.map(d => <div key={d} className="text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">{d}</div>)}</div>
        {rows}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-emerald-900 p-6 rounded-3xl text-white shadow-xl">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center"><SafeIcon icon={FiCalendar} className="text-2xl" /></div>
          <div><h2 className="text-xl font-bold">{t.availabilityStatus}</h2></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white/10 p-3 rounded-2xl"><p className="text-[10px] uppercase font-bold text-emerald-300 mb-1">{t.available}</p><div className="flex items-center gap-2"><div className="w-2 h-2 bg-emerald-400 rounded-full"></div><span className="text-sm font-bold">{t.free}</span></div></div>
          <div className="bg-white/10 p-3 rounded-2xl"><p className="text-[10px] uppercase font-bold text-emerald-300 mb-1">{t.occupied}</p><div className="flex items-center gap-2"><div className="w-2 h-2 bg-red-400 rounded-full"></div><span className="text-sm font-bold">{t.reserved}</span></div></div>
        </div>
      </div>
      {loading ? (
        <div className="py-20 flex flex-col items-center justify-center gap-4"><SafeIcon icon={FiLoader} className="text-4xl text-emerald-800 animate-spin" /><p className="text-gray-500 font-medium text-sm">{t.syncing}</p></div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 capitalize">{format(currentMonth, 'MMMM yyyy', { locale: currentLocale })}</h3>
            <div className="flex gap-2">
              <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 hover:bg-gray-100 rounded-xl"><SafeIcon icon={FiChevronLeft} /></button>
              <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 hover:bg-gray-100 rounded-xl"><SafeIcon icon={FiChevronRight} /></button>
            </div>
          </div>
          {renderCalendar()}
          <div className="mt-8">
            {selectedRange.start && selectedRange.end ? (
              <button onClick={() => onReserve({ checkIn: format(selectedRange.start, 'yyyy-MM-dd'), checkOut: format(selectedRange.end, 'yyyy-MM-dd') })} className="w-full bg-emerald-800 text-white font-black py-5 rounded-3xl shadow-2xl flex items-center justify-center gap-3 text-lg">
                <SafeIcon icon={FiCheckCircle} /> {t.reserveDates} <SafeIcon icon={FiArrowRight} />
              </button>
            ) : (
              <div className="bg-gray-50 p-6 rounded-3xl border-2 border-dashed border-gray-200 text-center">
                <p className="text-gray-500 text-sm font-medium">{t.selectDates}</p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}